import { Badge, Flex } from '@mantine/core';
import dayjs from 'dayjs';
import { ReactElement, useMemo } from 'react';
import { getWeekDaysNames } from 'utils/dates';
import { Timeframe } from './Calendar.helpers';
import classes from './Calendar.module.css';

interface CalendarEvent {
  id: string;
  date: number | string;
  value?: string;
}

interface CalendarProps {
  range: {
    startDate: Date;
    endDate: Date;
  };
  view?: Timeframe;
  events: CalendarEvent[];
  renderItem?: (event: CalendarEvent) => ReactElement;
}

export const Calendar = (props: CalendarProps) => {
  const { grid, header, dayNumber, leftLabel, cell, borderLeft, borderTop } = classes;
  const { range, events, renderItem } = props;

  const startOfWeek = dayjs(range.startDate).startOf('week').startOf('day');
  const endOfWeek = dayjs(range.endDate).endOf('week').endOf('day');
  const weekdays = getWeekDaysNames(startOfWeek.day());

  const allDays = useMemo(() => {
    const daysBetween = endOfWeek.diff(startOfWeek, 'days') + 1;

    const seenIds = new Set<string>();
    const uniqueEvents = events.filter((event) => {
      if (seenIds.has(event.id)) return false;
      seenIds.add(event.id);
      return true;
    });

    const days: { date: dayjs.Dayjs; events: CalendarEvent[] }[] = [];
    for (let i = 0; i < daysBetween; i += 1) {
      const newDate = startOfWeek.add(i, 'days');

      const matchingEvents = uniqueEvents.filter((event) => dayjs(event.date).isSame(newDate, 'day'));
      days.push({ date: newDate, events: matchingEvents });
    }

    return days;
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
  }, [endOfWeek, events, startOfWeek]);

  const leftLabels: { label?: string; row?: number }[] = useMemo(() => {
    const amountOfWeeks = Math.ceil(allDays.length / 7);

    const labels: { label?: string; row: number }[] = [];

    for (let i = 0; i < amountOfWeeks; i += 1) {
      const month = allDays[i * 7].date.format('MMMM');
      const year = allDays[i * 7].date.format('YYYY');
      const exists = labels.map((label) => label.label).includes(`${month} ${year}`);
      labels.push({ label: exists ? undefined : `${month} ${year}`, row: i });
    }

    return labels;
  }, [allDays]);

  return (
    <div className={grid}>
      <div />
      {weekdays.map((wd, i) => (
        <div key={wd} className={`${header} ${i === 0 && borderLeft}`}>
          {wd}
        </div>
      ))}
      {leftLabels.map((label) => (
        <div
          className={`${leftLabel} ${label.label && borderTop}`}
          key={label.row}
          style={{ gridRow: label.row && label.row + 2 }}
        >
          {label.label}
        </div>
      ))}
      {allDays.map((day) => (
        <div className={cell} key={day.date.valueOf()}>
          <span className={dayNumber}>{day.date.format('D')}</span>
          <Flex wrap={'wrap'} gap={3}>
            {day.events.map((e, i) =>
              renderItem ? (
                renderItem(e)
              ) : (
                <Badge mb={5} key={`${day.date.valueOf}-${e.value}-${i}`}>
                  {e.value}
                </Badge>
              ),
            )}
          </Flex>
        </div>
      ))}
    </div>
  );
};
